import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Stack,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  ContentCopy as CopyIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassIcon,
} from '@mui/icons-material';
// @ts-ignore
import * as XLSXStyle from 'xlsx-js-style';
import { guidedPresenceAPI, leaveAdminAPI } from '../services/api';

// --- Tipi ---
interface GroupEmployeePresence {
  employee_id: number;
  user_id: number;
  nome: string;
  cognome: string;
  matricola: string | null;
  presence_id: number | null;
  check_in: string | null;
  check_out: string | null;
  work_hours: number | null;
  expected_hours: number;
  status: 'present' | 'absent' | 'incomplete' | 'holiday' | 'leave';
  leave_request_id?: number;
  leave_type?: string;
  absence_hours?: number | null;
  leave_reason?: string | null;
}

interface DailyGroupPresence {
  date: string;
  day_of_week: string;
  is_holiday: boolean;
  is_weekend: boolean;
  employees: GroupEmployeePresence[];
  stats: { total_employees: number; present: number; absent: number; incomplete: number; on_leave: number };
}

interface DayPresenceCell {
  presence_id: number | null;
  check_in: string | null;
  check_out: string | null;
  work_hours: number | null;
  status: string;
  leave_request_id?: number;
  leave_type?: string;
  absence_hours?: number | null;
  leave_reason?: string | null;
  is_holiday: boolean;
  is_weekend: boolean;
}

interface WeeklyEmployee {
  employee_id: number;
  user_id: number;
  nome: string;
  cognome: string;
  matricola: string | null;
  expected_hours_daily: number;
  days: Record<string, DayPresenceCell>;
}

interface WeeklyGroupPresence {
  week_start: string;
  week_end: string;
  days: string[];
  day_labels: string[];
  employees: WeeklyEmployee[];
}

interface AbsenceCalendarData {
  year: number;
  month: number;
  month_name: string;
  group_name: string;
  employees: {
    employee_id: number;
    nome: string;
    cognome: string;
    matricola: string | null;
    absences: Record<string, string>; // "2026-02-05" -> "ferie"
  }[];
}

type ViewMode = 'daily' | 'weekly' | 'monthly';

type AbsenceType = '' | 'ferie' | 'permesso' | 'malattia';

interface EditedEntry {
  employee_id: number;
  user_id: number;
  nome: string;
  cognome: string;
  date: string;
  check_in: string;
  check_out: string;
  absence_type: AbsenceType;
  absence_hours: number;
  expected_hours: number;
  leave_reason: string;
  original_leave_request_id: number | null;
  original_leave_type: string | null;
  original_absence_hours: number;
  original_leave_reason: string;
  modified: boolean;
}

const LEAVE_REASONS = [
  'Studio',
  'ROL (Riduzione Orario Lavorativo)',
  'Lutto',
  'Matrimonio',
  'Malattia del figlio',
  'Esami',
  'Visite mediche',
  'Motivi personali',
  'Donazione sangue',
  'Accompagnamento familiare',
  'Adozione/Affidamento',
  'Congedo parentale',
  'Altro',
];

const absenceLabels: Record<string, string> = {
  ferie: 'Ferie (gg. intero)',
  permesso: 'Permesso',
  malattia: 'Malattia (gg. intero)',
};

const statusColors: Record<string, 'success' | 'error' | 'warning' | 'info' | 'default'> = {
  present: 'success',
  absent: 'error',
  incomplete: 'warning',
  holiday: 'default',
  leave: 'info',
};

const statusLabels: Record<string, string> = {
  present: 'Presente',
  absent: 'Assente',
  incomplete: 'Incompleto',
  holiday: 'Festivo',
  leave: 'In permesso',
};

const formatDate = (d: Date) => {
  const year = d.getFullYear();
  const month = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const normalizeAbsenceType = (value?: string | null): AbsenceType => {
  const normalized = (value || '').trim().toLowerCase();
  if (normalized === 'ferie') return 'ferie';
  if (normalized === 'permesso' || normalized === 'permessi') return 'permesso';
  if (normalized === 'malattia') return 'malattia';
  return '';
};

const getGuidedPresenceDraftStorageKey = (groupID: number) => `guided-presence-draft:${groupID}`;

const parseLocalDate = (dateStr: string): Date => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const getMonday = (d: Date): Date => {
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.getFullYear(), d.getMonth(), diff);
};

const getWeekStartForDate = (dateStr: string) => formatDate(getMonday(parseLocalDate(dateStr)));

interface MonthlyEmployeeSummary {
  employee_id: number;
  user_id: number;
  nome: string;
  cognome: string;
  matricola: string | null;
  expected_hours: number;
  worked_hours: number;
  overtime_hours: number;
  working_days: number;
  present_days: number;
  absent_days: number;
  ferie_days: number;
  permessi_hours: number;
  malattia_days: number;
  incomplete_days: number;
  delta_hours: number;
  is_validated: boolean;
}

interface MonthlyGroupSummary {
  year: number;
  month: number;
  month_name: string;
  group_id: number;
  group_name: string;
  employees: MonthlyEmployeeSummary[];
}

interface ManagedGroup {
  id: number;
  name: string;
}

const GuidedPresenceEntry: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('daily');
  const [selectedDate, setSelectedDate] = useState<string>(formatDate(new Date()));
  const [weekStart, setWeekStart] = useState<string>(formatDate(getMonday(new Date())));

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Gruppi gestiti
  const [groups, setGroups] = useState<ManagedGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<number>(0);
  const [groupsLoaded, setGroupsLoaded] = useState(false);

  // Riepilogo mensile
  const [monthlyYear, setMonthlyYear] = useState<number>(new Date().getFullYear());
  const [monthlyMonth, setMonthlyMonth] = useState<number>(new Date().getMonth() + 1);
  const [monthlyData, setMonthlyData] = useState<MonthlyGroupSummary | null>(null);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingAbsences, setExportingAbsences] = useState(false);
  const [applyingMonthlyTemplate, setApplyingMonthlyTemplate] = useState(false);

  // Dati
  const [dailyData, setDailyData] = useState<DailyGroupPresence | null>(null);
  const [weeklyData, setWeeklyData] = useState<WeeklyGroupPresence | null>(null);

  // Editing state - chiave: "employeeId-date"
  const [editedEntries, setEditedEntries] = useState<Record<string, EditedEntry>>({});
  const preserveModifiedOnNextLoadRef = useRef(false);

  // Selezione dipendenti
  const [selectedEmployees, setSelectedEmployees] = useState<Set<number>>(new Set());

  // Template orario
  const [templateCheckIn, setTemplateCheckIn] = useState('08:00');
  const [templateCheckOut, setTemplateCheckOut] = useState('17:00');

  const loadDraftEntriesForGroup = useCallback((groupID: number): Record<string, EditedEntry> => {
    if (!groupID) return {};
    try {
      const raw = localStorage.getItem(getGuidedPresenceDraftStorageKey(groupID));
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return {};
      return parsed as Record<string, EditedEntry>;
    } catch {
      return {};
    }
  }, []);

  const persistDraftEntriesForGroup = useCallback((groupID: number, entries: Record<string, EditedEntry>) => {
    if (!groupID) return;
    try {
      localStorage.setItem(getGuidedPresenceDraftStorageKey(groupID), JSON.stringify(entries));
    } catch {
      // ignore storage errors
    }
  }, []);

  const clearDraftEntriesForGroup = useCallback((groupID: number) => {
    if (!groupID) return;
    try {
      localStorage.removeItem(getGuidedPresenceDraftStorageKey(groupID));
    } catch {
      // ignore storage errors
    }
  }, []);

  // --- Caricamento gruppi ---
  useEffect(() => {
    const loadGroups = async () => {
      try {
        const data = await guidedPresenceAPI.getManagedGroups();
        setGroups(data || []);
        if (data && data.length > 0) {
          setSelectedGroupId(data[0].id);
        }
      } catch {
        setError('Errore caricamento gruppi');
      } finally {
        setGroupsLoaded(true);
      }
    };
    loadGroups();
  }, []);

  // --- Caricamento dati ---
  const loadDaily = useCallback(async (preserveModified = false) => {
    if (!groupsLoaded || selectedGroupId === 0) return;
    setLoading(true);
    setError(null);
    try {
      const data = await guidedPresenceAPI.getDailyPresences(selectedDate, selectedGroupId);
      setDailyData(data);
      initDailyEdits(data, preserveModified);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Errore caricamento presenze');
    } finally {
      setLoading(false);
    }
  }, [selectedDate, selectedGroupId, groupsLoaded]);

  const loadWeekly = useCallback(async (preserveModified = false) => {
    if (!groupsLoaded || selectedGroupId === 0) return;
    setLoading(true);
    setError(null);
    try {
      const data = await guidedPresenceAPI.getWeeklyPresences(weekStart, selectedGroupId);
      setWeeklyData(data);
      initWeeklyEdits(data, preserveModified);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Errore caricamento presenze');
    } finally {
      setLoading(false);
    }
  }, [weekStart, selectedGroupId, groupsLoaded]);

  const loadMonthly = useCallback(async () => {
    if (!groupsLoaded || selectedGroupId === 0) return;
    setMonthlyLoading(true);
    setError(null);
    try {
      const data = await guidedPresenceAPI.getMonthlySummary(monthlyYear, monthlyMonth, selectedGroupId);
      setMonthlyData(data);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Errore caricamento riepilogo mensile');
    } finally {
      setMonthlyLoading(false);
    }
  }, [monthlyYear, monthlyMonth, selectedGroupId, groupsLoaded]);

  useEffect(() => {
    const preserveModified = preserveModifiedOnNextLoadRef.current;
    preserveModifiedOnNextLoadRef.current = false;
    if (viewMode === 'daily') loadDaily(preserveModified);
    else if (viewMode === 'weekly') loadWeekly(preserveModified);
    else loadMonthly();
  }, [viewMode, loadDaily, loadWeekly, loadMonthly]);

  useEffect(() => {
    if (!selectedGroupId) return;
    const modifiedOnly = Object.fromEntries(
      Object.entries(editedEntries).filter(([, entry]) => entry?.modified),
    ) as Record<string, EditedEntry>;
    persistDraftEntriesForGroup(selectedGroupId, modifiedOnly);
  }, [editedEntries, selectedGroupId, persistDraftEntriesForGroup]);

  const syncSelectedEmployees = (employeeIds: number[]) => {
    const allowedIds = new Set(employeeIds);
    setSelectedEmployees((prev) => new Set([...prev].filter((id) => allowedIds.has(id))));
  };

  const currentVisibleEmployeeIds = viewMode === 'daily'
    ? (dailyData?.employees || []).map((employee) => employee.employee_id)
    : viewMode === 'weekly'
      ? (weeklyData?.employees || []).map((employee) => employee.employee_id)
      : [];
  const selectedVisibleEmployeeCount = currentVisibleEmployeeIds.filter((id) => selectedEmployees.has(id)).length;

  // --- Inizializzazione editing ---
  const mergeEditedEntry = (baseEntry: EditedEntry, existingEntry?: EditedEntry): EditedEntry => {
    if (!existingEntry?.modified) {
      return baseEntry;
    }

    return {
      ...baseEntry,
      check_in: existingEntry.check_in,
      check_out: existingEntry.check_out,
      absence_type: existingEntry.absence_type,
      absence_hours: existingEntry.absence_hours,
      leave_reason: existingEntry.leave_reason,
      modified: true,
    };
  };

  const initDailyEdits = (data: DailyGroupPresence, preserveModified = false) => {
    setEditedEntries((prev) => {
      const sourceEntries = Object.keys(prev).length > 0 ? prev : loadDraftEntriesForGroup(selectedGroupId);
      const entries: Record<string, EditedEntry> = { ...sourceEntries };
      data.employees.forEach((emp) => {
        const key = `${emp.employee_id}-${data.date}`;
        const leaveType = normalizeAbsenceType(emp.leave_type);
        const baseEntry: EditedEntry = {
          employee_id: emp.employee_id,
          user_id: emp.user_id,
          nome: emp.nome,
          cognome: emp.cognome,
          date: data.date,
          check_in: emp.check_in || '',
          check_out: emp.check_out || '',
          absence_type: leaveType,
          absence_hours: emp.absence_hours ?? (leaveType === 'permesso' ? 1 : (emp.expected_hours || 8)),
          expected_hours: emp.expected_hours || 8,
          leave_reason: emp.leave_reason ?? '',
          original_leave_request_id: emp.leave_request_id ?? null,
          original_leave_type: leaveType || null,
          original_absence_hours: emp.absence_hours ?? (leaveType === 'permesso' ? 1 : (emp.expected_hours || 8)),
          original_leave_reason: emp.leave_reason ?? '',
          modified: false,
        };
        const shouldPreserve = preserveModified || !!sourceEntries[key]?.modified;
        entries[key] = shouldPreserve ? mergeEditedEntry(baseEntry, sourceEntries[key]) : baseEntry;
      });
      return entries;
    });
    syncSelectedEmployees(data.employees.map((employee) => employee.employee_id));
  };

  const initWeeklyEdits = (data: WeeklyGroupPresence, preserveModified = false) => {
    setEditedEntries((prev) => {
      const sourceEntries = Object.keys(prev).length > 0 ? prev : loadDraftEntriesForGroup(selectedGroupId);
      const entries: Record<string, EditedEntry> = { ...sourceEntries };
      data.employees.forEach((emp) => {
        data.days.forEach((dayStr) => {
          const key = `${emp.employee_id}-${dayStr}`;
          const cell = emp.days[dayStr];
          const leaveType = normalizeAbsenceType(cell?.leave_type);
          const baseEntry: EditedEntry = {
            employee_id: emp.employee_id,
            user_id: emp.user_id,
            nome: emp.nome,
            cognome: emp.cognome,
            date: dayStr,
            check_in: cell?.check_in || '',
            check_out: cell?.check_out || '',
            absence_type: leaveType,
            absence_hours: cell?.absence_hours ?? (leaveType === 'permesso' ? 1 : (emp.expected_hours_daily || 8)),
            expected_hours: emp.expected_hours_daily || 8,
            leave_reason: cell?.leave_reason ?? '',
            original_leave_request_id: cell?.leave_request_id ?? null,
            original_leave_type: leaveType || null,
            original_absence_hours: cell?.absence_hours ?? (leaveType === 'permesso' ? 1 : (emp.expected_hours_daily || 8)),
            original_leave_reason: cell?.leave_reason ?? '',
            modified: false,
          };
          const shouldPreserve = preserveModified || !!sourceEntries[key]?.modified;
          entries[key] = shouldPreserve ? mergeEditedEntry(baseEntry, sourceEntries[key]) : baseEntry;
        });
      });
      return entries;
    });
    syncSelectedEmployees(data.employees.map((employee) => employee.employee_id));
  };

  // --- Editing ---
  const handleFieldChange = (key: string, field: 'check_in' | 'check_out', value: string) => {
    setEditedEntries((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value, modified: true },
    }));
  };

  const handleAbsenceChange = (key: string, absenceType: AbsenceType) => {
    setEditedEntries((prev) => {
      const entry = prev[key];
      if (absenceType) {
        return {
          ...prev,
          [key]: {
            ...entry,
            absence_type: absenceType,
            check_in: '',
            check_out: '',
            absence_hours: absenceType === 'permesso' ? 1 : entry.expected_hours,
            leave_reason: absenceType === 'permesso' ? entry.leave_reason : '',
            modified: true,
          },
        };
      } else {
        return {
          ...prev,
          [key]: { ...entry, absence_type: '' as AbsenceType, leave_reason: '', absence_hours: 1, modified: true },
        };
      }
    });
  };

  const handleLeaveReasonChange = (key: string, reason: string) => {
    setEditedEntries((prev) => ({
      ...prev,
      [key]: { ...prev[key], leave_reason: reason, modified: true },
    }));
  };

  const handleAbsenceHoursChange = (key: string, hours: number) => {
    setEditedEntries((prev) => ({
      ...prev,
      [key]: { ...prev[key], absence_hours: hours, modified: true },
    }));
  };

  const modifiedEntries = Object.values(editedEntries).filter((e) => e.modified);

  // --- Template ---
  // --- Selezione dipendenti ---
  const toggleEmployee = (empId: number) => {
    setSelectedEmployees((prev) => {
      const next = new Set(prev);
      if (next.has(empId)) next.delete(empId);
      else next.add(empId);
      return next;
    });
  };

  const toggleAllEmployees = () => {
    const allIds = viewMode === 'daily'
      ? (dailyData?.employees || []).map((e) => e.employee_id)
      : (weeklyData?.employees || []).map((e) => e.employee_id);
    
    if (selectedVisibleEmployeeCount === allIds.length) {
      setSelectedEmployees(new Set());
    } else {
      setSelectedEmployees(new Set(allIds));
    }
  };

  const isEmployeeSelected = (empId: number) => selectedEmployees.size === 0 || selectedEmployees.has(empId);

  // --- Template ---
  const applyTemplateToAbsent = () => {
    setEditedEntries((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        const entry = next[key];
        if (!isEmployeeSelected(entry.employee_id)) return;
        if (!entry.check_in && !entry.check_out && !entry.absence_type) {
          const d = new Date(entry.date + 'T00:00:00');
          const isWeekend = d.getDay() === 0 || d.getDay() === 6;
          if (!isWeekend) {
            next[key] = { ...entry, check_in: templateCheckIn, check_out: templateCheckOut, modified: true };
          }
        }
      });
      return next;
    });
  };

  const applyTemplateToAll = () => {
    setEditedEntries((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        const entry = next[key];
        if (!isEmployeeSelected(entry.employee_id)) return;
        const d = new Date(entry.date + 'T00:00:00');
        const isWeekend = d.getDay() === 0 || d.getDay() === 6;
        if (!isWeekend) {
          next[key] = { ...entry, check_in: templateCheckIn, check_out: templateCheckOut, absence_type: '' as AbsenceType, modified: true };
        }
      });
      return next;
    });
  };

  // --- Copia riga (settimanale) ---
  const copyDayToWeek = (employeeId: number, sourceDate: string) => {
    const sourceKey = `${employeeId}-${sourceDate}`;
    const source = editedEntries[sourceKey];
    if (!source || (!source.check_in && !source.check_out)) return;

    setEditedEntries((prev) => {
      const next = { ...prev };
      const days = weeklyData?.days || [];
      days.forEach((dayStr) => {
        if (dayStr === sourceDate) return;
        const d = new Date(dayStr + 'T00:00:00');
        if (d.getDay() === 0 || d.getDay() === 6) return;
        const key = `${employeeId}-${dayStr}`;
        if (next[key]) {
          next[key] = { ...next[key], check_in: source.check_in, check_out: source.check_out, modified: true };
        }
      });
      return next;
    });
  };

  // --- Riepilogo modifiche ---
  const isPermessoDetailsChanged = (entry: EditedEntry) => {
    return (
      entry.absence_type === 'permesso' &&
      entry.original_leave_type === 'permesso' &&
      (entry.absence_hours !== entry.original_absence_hours ||
        (entry.leave_reason || '').trim() !== (entry.original_leave_reason || '').trim())
    );
  };

  const getSaveSummary = () => {
    const presences = modifiedEntries.filter((e) => !e.absence_type && (e.check_in || e.check_out));
    const ferieEntries = modifiedEntries.filter((e) => e.absence_type === 'ferie' && e.absence_type !== e.original_leave_type);
    const malattiaEntries = modifiedEntries.filter((e) => e.absence_type === 'malattia' && e.absence_type !== e.original_leave_type);
    const permessoEntries = modifiedEntries.filter((e) => e.absence_type === 'permesso' && (e.absence_type !== e.original_leave_type || isPermessoDetailsChanged(e)));
    const deletedLeaveEntries = modifiedEntries.filter((e) => !e.absence_type && !!e.original_leave_request_id);
    return { presences, ferieEntries, malattiaEntries, permessoEntries, deletedLeaveEntries };
  };

  const handleRequestSave = () => {
    if (modifiedEntries.length === 0) return;
    const { presences, ferieEntries, malattiaEntries, permessoEntries, deletedLeaveEntries } = getSaveSummary();
    if (presences.length === 0 && ferieEntries.length === 0 && malattiaEntries.length === 0 && permessoEntries.length === 0 && deletedLeaveEntries.length === 0) {
      setSuccess('Nessuna modifica da salvare');
      return;
    }

    // Verifica che i permessi abbiano tutti tipologia e ore
    const invalidPermessi = permessoEntries.filter((e) => !e.leave_reason || e.absence_hours <= 0);
    if (invalidPermessi.length > 0) {
      setError(`${invalidPermessi.length} permesso/i senza tipologia o ore. Compila tutti i campi.`);
      return;
    }

    setConfirmDialogOpen(true);
  };

  // --- Salvataggio effettivo ---
  const handleConfirmSave = async () => {
    setConfirmDialogOpen(false);
    setSaving(true);
    setError(null);
    setSuccess(null);

    const { presences, ferieEntries, malattiaEntries, permessoEntries, deletedLeaveEntries } = getSaveSummary();

    const presencePayload = presences.map((e) => ({
      employee_id: e.employee_id,
      user_id: e.user_id,
      date: e.date,
      check_in: e.check_in || null,
      check_out: e.check_out || null,
    }));

    const allAbsences = [...ferieEntries, ...malattiaEntries, ...permessoEntries];

    let deleteMsg = '';
    let presenceMsg = '';
    let absenceMsg = '';
    let hasErrors = false;

    try {
      // Cancella assenze esistenti quando vengono rimosse o modificate (tipo/ore/causale)
      const leaveRequestIdsToDelete = new Set<number>();
      deletedLeaveEntries.forEach((entry) => {
        if (entry.original_leave_request_id) leaveRequestIdsToDelete.add(entry.original_leave_request_id);
      });
      [...ferieEntries, ...malattiaEntries, ...permessoEntries].forEach((entry) => {
        if (entry.original_leave_request_id) leaveRequestIdsToDelete.add(entry.original_leave_request_id);
      });

      let deletedOk = 0;
      let deletedFail = 0;
      for (const leaveRequestID of leaveRequestIdsToDelete) {
        try {
          await leaveAdminAPI.deleteLeave(leaveRequestID);
          deletedOk++;
        } catch {
          deletedFail++;
          hasErrors = true;
        }
      }
      if (deletedOk > 0 || deletedFail > 0) {
        deleteMsg = `Assenze rimosse: ${deletedOk}`;
        if (deletedFail > 0) deleteMsg += `, ${deletedFail} errori`;
      }

      if (presencePayload.length > 0) {
        const result = await guidedPresenceAPI.bulkSave(presencePayload);
        presenceMsg = `Presenze: ${result.total_created} create, ${result.total_updated} aggiornate`;
        if (result.total_failed > 0) {
          presenceMsg += `, ${result.total_failed} errori`;
          hasErrors = true;
        }
      }

      let absenceOk = 0;
      let absenceFail = 0;
      for (const entry of allAbsences) {
        try {
          const isFullDay = entry.absence_type === 'ferie' || entry.absence_type === 'malattia';
          await leaveAdminAPI.createLeaveForEmployee(entry.employee_id, {
            request_type: entry.absence_type as 'ferie' | 'permesso' | 'malattia' | 'altri',
            start_date: entry.date,
            duration_hours: isFullDay ? undefined : entry.absence_hours,
            notes: `Inserito da admin tramite gestione presenze gruppo`,
            leave_reason: entry.absence_type === 'permesso' ? entry.leave_reason : undefined,
          });
          absenceOk++;
        } catch {
          absenceFail++;
          hasErrors = true;
        }
      }
      if (absenceOk > 0 || absenceFail > 0) {
        absenceMsg = `Assenze: ${absenceOk} registrate`;
        if (absenceFail > 0) absenceMsg += `, ${absenceFail} errori`;
      }

      const msg = [deleteMsg, presenceMsg, absenceMsg].filter(Boolean).join(' | ');
      if (hasErrors) setError(msg);
      else {
        setSuccess(msg || 'Salvato con successo');
        clearDraftEntriesForGroup(selectedGroupId);
      }

      if (viewMode === 'daily') await loadDaily();
      else await loadWeekly();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  // --- Navigazione date ---
  const navigateDay = (offset: number) => {
    preserveModifiedOnNextLoadRef.current = true;
    const d = parseLocalDate(selectedDate);
    d.setDate(d.getDate() + offset);
    setSelectedDate(formatDate(d));
  };

  const navigateWeek = (offset: number) => {
    preserveModifiedOnNextLoadRef.current = true;
    const d = parseLocalDate(weekStart);
    d.setDate(d.getDate() + offset * 7);
    setWeekStart(formatDate(d));
  };

  const handleViewModeChange = (_: React.SyntheticEvent, nextViewMode: ViewMode) => {
    if (nextViewMode === viewMode) {
      return;
    }

    if ((viewMode === 'daily' && nextViewMode === 'weekly') || (viewMode === 'weekly' && nextViewMode === 'daily')) {
      preserveModifiedOnNextLoadRef.current = true;
    }

    if (nextViewMode === 'weekly') {
      setWeekStart(getWeekStartForDate(selectedDate));
    }

    setViewMode(nextViewMode);
  };

  const applyTemplateToMonthly = async () => {
    if (!groupsLoaded || selectedGroupId === 0) {
      setError('Nessun gruppo selezionato');
      return;
    }

    setApplyingMonthlyTemplate(true);
    setError(null);
    setSuccess(null);

    try {
      const daysInMonth = new Date(monthlyYear, monthlyMonth, 0).getDate();
      const payload: Array<{
        employee_id: number;
        user_id: number;
        date: string;
        check_in: string | null;
        check_out: string | null;
      }> = [];

      for (let day = 1; day <= daysInMonth; day++) {
        const dateObj = new Date(monthlyYear, monthlyMonth - 1, day);
        if (isItalianHolidayJS(dateObj)) continue;

        const dateStr = `${monthlyYear}-${String(monthlyMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        const daily = await guidedPresenceAPI.getDailyPresences(dateStr, selectedGroupId);
        const employees = Array.isArray(daily?.employees) ? daily.employees : [];

        employees.forEach((emp) => {
          const hasLeave = !!emp.leave_type;
          const hasPresence = !!emp.check_in || !!emp.check_out;
          if (!hasLeave && !hasPresence) {
            payload.push({
              employee_id: emp.employee_id,
              user_id: emp.user_id,
              date: dateStr,
              check_in: templateCheckIn || null,
              check_out: templateCheckOut || null,
            });
          }
        });
      }

      if (payload.length === 0) {
        setSuccess('Nessun assente da compilare nel mese selezionato');
        return;
      }

      const result = await guidedPresenceAPI.bulkSave(payload);
      setSuccess(
        `Applica tutti mensile completato: ${result.total_created} create, ${result.total_updated} aggiornate, ${result.total_failed} errori`
      );
      await loadMonthly();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Errore durante applica tutti mensile');
    } finally {
      setApplyingMonthlyTemplate(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Inserimento Presenze di Gruppo
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Gestisci le presenze dei dipendenti assegnati al tuo perimetro amministrativo
      </Typography>

      {/* Selettore Gruppo */}
      {groups.length > 1 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 300 }}>
            <InputLabel>Gruppo</InputLabel>
            <Select
              value={selectedGroupId}
              label="Gruppo"
              onChange={(e) => setSelectedGroupId(Number(e.target.value))}
            >
              {groups.map((g) => (
                <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>
      )}

      {groups.length === 0 && groupsLoaded && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Non sei assegnato a nessun gruppo con gestione presenze admin. Contatta un super admin per l'assegnazione.
        </Alert>
      )}

      {/* Tab Vista */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={viewMode} onChange={handleViewModeChange}>
          <Tab value="daily" label="Vista Giornaliera" />
          <Tab value="weekly" label="Vista Settimanale" />
          <Tab value="monthly" label="Riepilogo Mensile" />
        </Tabs>
      </Paper>

      {/* Navigazione + Template (solo vista giornaliera/settimanale) */}
      {viewMode !== 'monthly' && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton onClick={() => viewMode === 'daily' ? navigateDay(-1) : navigateWeek(-1)}>
                <PrevIcon />
              </IconButton>
              {viewMode === 'daily' ? (
                <TextField
                  type="date"
                  size="small"
                  value={selectedDate}
                  onChange={(e) => {
                    preserveModifiedOnNextLoadRef.current = true;
                    setSelectedDate(e.target.value);
                  }}
                  sx={{ width: 180 }}
                />
              ) : (
                <Typography variant="h6">
                  {weeklyData?.day_labels?.[0]} - {weeklyData?.day_labels?.[4]}
                </Typography>
              )}
              <IconButton onClick={() => viewMode === 'daily' ? navigateDay(1) : navigateWeek(1)}>
                <NextIcon />
              </IconButton>
              {viewMode === 'daily' && dailyData && (
                <Chip label={dailyData.day_of_week} variant="outlined" />
              )}
              <IconButton onClick={() => {
                preserveModifiedOnNextLoadRef.current = true;
                if (viewMode === 'daily') loadDaily(true);
                else loadWeekly(true);
              }}>
                <RefreshIcon />
              </IconButton>
            </Box>

            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <ScheduleIcon color="action" />
              <Typography variant="body2" color="text.secondary">Template:</Typography>
              <TextField
                type="time"
                size="small"
                value={templateCheckIn}
                onChange={(e) => setTemplateCheckIn(e.target.value)}
                sx={{ width: 110 }}
              />
              <Typography variant="body2">-</Typography>
              <TextField
                type="time"
                size="small"
                value={templateCheckOut}
                onChange={(e) => setTemplateCheckOut(e.target.value)}
                sx={{ width: 110 }}
              />
              <Tooltip title={selectedVisibleEmployeeCount > 0 ? `Applica ai selezionati senza presenza (${selectedVisibleEmployeeCount})` : 'Applica a tutti senza presenza'}>
                <Button size="small" variant="outlined" onClick={applyTemplateToAbsent}>
                  Applica assenti{selectedVisibleEmployeeCount > 0 ? ` (${selectedVisibleEmployeeCount})` : ''}
                </Button>
              </Tooltip>
              <Tooltip title={selectedVisibleEmployeeCount > 0 ? `Applica ai selezionati (${selectedVisibleEmployeeCount})` : 'Applica a tutti (sovrascrive)'}>
                <Button size="small" variant="outlined" color="warning" onClick={applyTemplateToAll}>
                  Applica{selectedVisibleEmployeeCount > 0 ? ` selezionati (${selectedVisibleEmployeeCount})` : ' tutti'}
                </Button>
              </Tooltip>
            </Box>
          </Box>

          {viewMode === 'daily' && dailyData && !dailyData.is_holiday && !dailyData.is_weekend && (
            <Box display="flex" gap={2} mt={2}>
              <Chip label={`Totale: ${dailyData.stats.total_employees}`} variant="outlined" />
              <Chip label={`Presenti: ${dailyData.stats.present}`} color="success" size="small" />
              <Chip label={`Assenti: ${dailyData.stats.absent}`} color="error" size="small" />
              <Chip label={`Incompleti: ${dailyData.stats.incomplete}`} color="warning" size="small" />
              {dailyData.stats.on_leave > 0 && <Chip label={`In permesso: ${dailyData.stats.on_leave}`} color="info" size="small" />}
            </Box>
          )}
        </Paper>
      )}

      {/* Messaggi */}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Avviso festivo/weekend */}
      {viewMode === 'daily' && dailyData && (dailyData.is_holiday || dailyData.is_weekend) && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {dailyData.is_holiday ? 'Giorno festivo' : 'Weekend'} - Non sono richieste presenze
        </Alert>
      )}

      {viewMode === 'monthly' ? (
        <MonthlyView
          data={monthlyData}
          loading={monthlyLoading}
          exporting={exporting}
          exportingAbsences={exportingAbsences}
          applyingMonthlyTemplate={applyingMonthlyTemplate}
          year={monthlyYear}
          month={monthlyMonth}
          selectedGroupId={selectedGroupId}
          onYearChange={setMonthlyYear}
          onMonthChange={setMonthlyMonth}
          onRefresh={loadMonthly}
          onApplyAll={applyTemplateToMonthly}
          onExport={async () => {
            if (!monthlyData || monthlyData.employees.length === 0) return;
            setExporting(true);
            try {
              exportMonthlySummary(monthlyData);
            } finally {
              setExporting(false);
            }
          }}
          onExportAbsences={async () => {
            setExportingAbsences(true);
            setError(null);
            try {
              const calendarData: AbsenceCalendarData = await guidedPresenceAPI.getMonthlyAbsenceCalendar(
                monthlyYear, monthlyMonth, selectedGroupId || undefined
              );
              if (!calendarData || calendarData.employees.length === 0) {
                setError('Nessun dato assenze trovato per il periodo selezionato');
                return;
              }
              exportAbsenceCalendar(calendarData);
            } catch (err: any) {
              setError(err?.response?.data?.error || 'Errore esportazione calendario assenze');
            } finally {
              setExportingAbsences(false);
            }
          }}
        />
      ) : loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : viewMode === 'daily' ? (
        <DailyView
          data={dailyData}
          editedEntries={editedEntries}
          selectedEmployees={selectedEmployees}
          onFieldChange={handleFieldChange}
          onAbsenceChange={handleAbsenceChange}
          onLeaveReasonChange={handleLeaveReasonChange}
          onAbsenceHoursChange={handleAbsenceHoursChange}
          onToggleEmployee={toggleEmployee}
          onToggleAll={toggleAllEmployees}
        />
      ) : (
        <WeeklyView
          data={weeklyData}
          editedEntries={editedEntries}
          selectedEmployees={selectedEmployees}
          onFieldChange={handleFieldChange}
          onAbsenceChange={handleAbsenceChange}
          onLeaveReasonChange={handleLeaveReasonChange}
          onAbsenceHoursChange={handleAbsenceHoursChange}
          onToggleEmployee={toggleEmployee}
          onToggleAll={toggleAllEmployees}
          onCopyDayToWeek={copyDayToWeek}
        />
      )}

      {/* Barra salvataggio (solo vista giornaliera/settimanale) */}
      {viewMode !== 'monthly' && modifiedEntries.length > 0 && (
        <Paper
          sx={{
            position: 'sticky', bottom: 16, p: 2, mt: 2,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            boxShadow: 4, zIndex: 10, bgcolor: 'background.paper',
          }}
        >
          <Typography>
            <strong>{modifiedEntries.length}</strong> modifiche in sospeso
          </Typography>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={() => viewMode === 'daily' ? loadDaily() : loadWeekly()}
            >
              Annulla
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleRequestSave}
              disabled={saving}
            >
              Salva Tutto ({modifiedEntries.filter(e => e.check_in || e.check_out || e.absence_type).length})
            </Button>
          </Box>
        </Paper>
      )}

      {/* Dialog di conferma */}
      <ConfirmSaveDialog
        open={confirmDialogOpen}
        summary={getSaveSummary()}
        onConfirm={handleConfirmSave}
        onCancel={() => setConfirmDialogOpen(false)}
      />
    </Container>
  );
};

// --- Dialog di Conferma Salvataggio ---
interface ConfirmSaveDialogProps {
  open: boolean;
  summary: {
    presences: EditedEntry[];
    ferieEntries: EditedEntry[];
    malattiaEntries: EditedEntry[];
    permessoEntries: EditedEntry[];
    deletedLeaveEntries: EditedEntry[];
  };
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmSaveDialog: React.FC<ConfirmSaveDialogProps> = ({ open, summary, onConfirm, onCancel }) => {
  const { presences, ferieEntries, malattiaEntries, permessoEntries, deletedLeaveEntries } = summary;
  const totalOps = presences.length + ferieEntries.length + malattiaEntries.length + permessoEntries.length + deletedLeaveEntries.length;

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Conferma Salvataggio</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body1" gutterBottom>
          Stai per salvare <strong>{totalOps}</strong> operazioni:
        </Typography>

        {presences.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Presenze ({presences.length})
            </Typography>
            {presences.slice(0, 10).map((e, i) => (
              <Typography key={i} variant="body2" color="text.secondary">
                {e.cognome} {e.nome} - {e.date}: {e.check_in} - {e.check_out}
              </Typography>
            ))}
            {presences.length > 10 && (
              <Typography variant="body2" color="text.secondary">
                ...e altre {presences.length - 10} presenze
              </Typography>
            )}
          </Box>
        )}

        {ferieEntries.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="info.main" gutterBottom>
              Ferie - giornata intera ({ferieEntries.length})
            </Typography>
            {ferieEntries.map((e, i) => (
              <Typography key={i} variant="body2" color="text.secondary">
                {e.cognome} {e.nome} - {e.date} ({e.expected_hours}h)
              </Typography>
            ))}
          </Box>
        )}

        {malattiaEntries.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="warning.main" gutterBottom>
              Malattia - giornata intera ({malattiaEntries.length})
            </Typography>
            {malattiaEntries.map((e, i) => (
              <Typography key={i} variant="body2" color="text.secondary">
                {e.cognome} {e.nome} - {e.date} ({e.expected_hours}h)
              </Typography>
            ))}
          </Box>
        )}

        {permessoEntries.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="secondary" gutterBottom>
              Permessi ({permessoEntries.length})
            </Typography>
            {permessoEntries.map((e, i) => (
              <Typography key={i} variant="body2" color="text.secondary">
                {e.cognome} {e.nome} - {e.date}: {e.leave_reason} ({e.absence_hours}h)
              </Typography>
            ))}
          </Box>
        )}

        {deletedLeaveEntries.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="error.main" gutterBottom>
              Assenze rimosse ({deletedLeaveEntries.length})
            </Typography>
            {deletedLeaveEntries.map((e, i) => (
              <Typography key={i} variant="body2" color="text.secondary">
                {e.cognome} {e.nome} - {e.date}
              </Typography>
            ))}
          </Box>
        )}

        <Alert severity="info" sx={{ mt: 2 }}>
          Le ferie e le malattie verranno scalate come giornata intera in base alle ore giornaliere del dipendente.
          I permessi verranno registrati con la tipologia e le ore indicate.
        </Alert>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onCancel} variant="outlined">Annulla</Button>
        <Button onClick={onConfirm} variant="contained" color="primary">
          Conferma Salvataggio
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// --- Sotto-componente Vista Giornaliera ---
interface DailyViewProps {
  data: DailyGroupPresence | null;
  editedEntries: Record<string, EditedEntry>;
  selectedEmployees: Set<number>;
  onFieldChange: (key: string, field: 'check_in' | 'check_out', value: string) => void;
  onAbsenceChange: (key: string, absenceType: AbsenceType) => void;
  onLeaveReasonChange: (key: string, reason: string) => void;
  onAbsenceHoursChange: (key: string, hours: number) => void;
  onToggleEmployee: (empId: number) => void;
  onToggleAll: () => void;
}

const DailyView: React.FC<DailyViewProps> = ({ data, editedEntries, selectedEmployees, onFieldChange, onAbsenceChange, onLeaveReasonChange, onAbsenceHoursChange, onToggleEmployee, onToggleAll }) => {
  if (!data || data.employees.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          Nessun dipendente trovato nei tuoi gruppi con gestione cartellini admin.
        </Typography>
      </Paper>
    );
  }

  const selectedCount = data.employees.filter((employee) => selectedEmployees.has(employee.employee_id)).length;

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'primary.main' }}>
            <TableCell padding="checkbox" sx={{ bgcolor: 'primary.main' }}>
              <Checkbox
                sx={{ color: 'white', '&.Mui-checked': { color: 'white' } }}
                checked={data.employees.length > 0 && selectedCount === data.employees.length}
                indeterminate={selectedCount > 0 && selectedCount < data.employees.length}
                onChange={onToggleAll}
              />
            </TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 700 }}>Dipendente</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 700 }} align="center">Tipo giornata</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 700 }} align="center">Dettaglio</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 700 }} align="right">Ore</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 700 }} align="right">Ore/gg</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.employees.map((emp) => {
            const key = `${emp.employee_id}-${data.date}`;
            const edited = editedEntries[key];
            const isModified = edited?.modified;
            const isHolidayOrWeekend = data.is_holiday || data.is_weekend;
            const absType = edited?.absence_type;

            return (
              <TableRow
                key={emp.employee_id}
                sx={{
                  bgcolor: isModified
                    ? 'rgba(25, 118, 210, 0.06)'
                    : absType ? 'rgba(33, 150, 243, 0.04)' : 'inherit',
                }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedEmployees.has(emp.employee_id)}
                    onChange={() => onToggleEmployee(emp.employee_id)}
                  />
                </TableCell>
                <TableCell>
                  <Typography fontWeight={600}>{emp.cognome} {emp.nome}</Typography>
                  <Typography variant="caption" color="text.secondary">{emp.matricola || ''}</Typography>
                </TableCell>
                <TableCell align="center">
                  {isHolidayOrWeekend ? (
                    <Chip label="Festivo" size="small" />
                  ) : (
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                      <Select
                        value={absType || ''}
                        onChange={(e) => onAbsenceChange(key, e.target.value as AbsenceType)}
                        displayEmpty
                        sx={{ fontSize: '0.8rem' }}
                      >
                        <MenuItem value="">Presente</MenuItem>
                        <MenuItem value="ferie">Ferie (gg. intero)</MenuItem>
                        <MenuItem value="permesso">Permesso (ore)</MenuItem>
                        <MenuItem value="malattia">Malattia (gg. intero)</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                </TableCell>
                <TableCell align="center">
                  {isHolidayOrWeekend ? (
                    <Typography variant="body2" color="text.disabled">-</Typography>
                  ) : !absType ? (
                    <Box display="flex" gap={0.5} justifyContent="center">
                      <TextField
                        type="time"
                        size="small"
                        value={edited?.check_in || ''}
                        onChange={(e) => onFieldChange(key, 'check_in', e.target.value)}
                        sx={{ width: 110 }}
                        InputProps={{ sx: { fontSize: '0.85rem' } }}
                        placeholder="Ingresso"
                      />
                      <TextField
                        type="time"
                        size="small"
                        value={edited?.check_out || ''}
                        onChange={(e) => onFieldChange(key, 'check_out', e.target.value)}
                        sx={{ width: 110 }}
                        InputProps={{ sx: { fontSize: '0.85rem' } }}
                        placeholder="Uscita"
                      />
                    </Box>
                  ) : absType === 'ferie' || absType === 'malattia' ? (
                    <Chip
                      label={`Giornata intera (${emp.expected_hours}h)`}
                      color={absType === 'ferie' ? 'info' : 'warning'}
                      size="small"
                      variant="outlined"
                    />
                  ) : absType === 'permesso' ? (
                    <Box display="flex" gap={0.5} alignItems="center" justifyContent="center">
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select
                          value={edited?.leave_reason || ''}
                          onChange={(e) => onLeaveReasonChange(key, e.target.value)}
                          displayEmpty
                          sx={{ fontSize: '0.75rem' }}
                        >
                          <MenuItem value="" disabled>Tipologia...</MenuItem>
                          {LEAVE_REASONS.map((r) => (
                            <MenuItem key={r} value={r} sx={{ fontSize: '0.8rem' }}>{r}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        type="number"
                        size="small"
                        value={edited?.absence_hours || ''}
                        onChange={(e) => onAbsenceHoursChange(key, parseFloat(e.target.value) || 0)}
                        sx={{ width: 70 }}
                        InputProps={{ sx: { fontSize: '0.85rem' }, inputProps: { min: 0.5, max: emp.expected_hours, step: 0.5 } }}
                      />
                      <Typography variant="caption">h</Typography>
                    </Box>
                  ) : null}
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={emp.work_hours ? 600 : 400}>
                    {emp.work_hours != null ? `${emp.work_hours.toFixed(2)}h` : '-'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color="text.secondary">
                    {emp.expected_hours > 0 ? `${emp.expected_hours.toFixed(1)}h` : '-'}
                  </Typography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// --- Sotto-componente Vista Settimanale ---
interface WeeklyViewProps {
  data: WeeklyGroupPresence | null;
  editedEntries: Record<string, EditedEntry>;
  selectedEmployees: Set<number>;
  onFieldChange: (key: string, field: 'check_in' | 'check_out', value: string) => void;
  onAbsenceChange: (key: string, absenceType: AbsenceType) => void;
  onLeaveReasonChange: (key: string, reason: string) => void;
  onAbsenceHoursChange: (key: string, hours: number) => void;
  onToggleEmployee: (empId: number) => void;
  onToggleAll: () => void;
  onCopyDayToWeek: (employeeId: number, sourceDate: string) => void;
}

const WeeklyView: React.FC<WeeklyViewProps> = ({ data, editedEntries, selectedEmployees, onFieldChange, onAbsenceChange, onLeaveReasonChange, onAbsenceHoursChange, onToggleEmployee, onToggleAll, onCopyDayToWeek }) => {
  if (!data || data.employees.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          Nessun dipendente trovato nei tuoi gruppi con gestione cartellini admin.
        </Typography>
      </Paper>
    );
  }

  const selectedCount = data.employees.filter((employee) => selectedEmployees.has(employee.employee_id)).length;

  return (
    <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'primary.main' }}>
            <TableCell padding="checkbox" sx={{ position: 'sticky', left: 0, bgcolor: 'primary.main', zIndex: 3 }}>
              <Checkbox
                sx={{ color: 'white', '&.Mui-checked': { color: 'white' } }}
                checked={data.employees.length > 0 && selectedCount === data.employees.length}
                indeterminate={selectedCount > 0 && selectedCount < data.employees.length}
                onChange={onToggleAll}
              />
            </TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 700, minWidth: 160, position: 'sticky', left: 48, bgcolor: 'primary.main', zIndex: 2 }}>
              Dipendente
            </TableCell>
            {data.day_labels.map((label, i) => (
              <TableCell key={i} align="center" sx={{ color: 'white', fontWeight: 700, minWidth: 150 }}>
                {label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.employees.map((emp) => (
            <TableRow key={emp.employee_id} hover>
              <TableCell padding="checkbox" sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                <Checkbox
                  checked={selectedEmployees.has(emp.employee_id)}
                  onChange={() => onToggleEmployee(emp.employee_id)}
                />
              </TableCell>
              <TableCell sx={{ position: 'sticky', left: 48, bgcolor: 'background.paper', zIndex: 1, borderRight: '1px solid rgba(0,0,0,0.12)' }}>
                <Typography variant="body2" fontWeight={600}>{emp.cognome} {emp.nome}</Typography>
                <Typography variant="caption" color="text.secondary">{emp.matricola || ''}</Typography>
              </TableCell>
              {data.days.map((dayStr) => {
                const key = `${emp.employee_id}-${dayStr}`;
                const edited = editedEntries[key];
                const cell = emp.days[dayStr];
                const isDisabled = cell?.is_holiday || cell?.is_weekend;
                const isModified = edited?.modified;
                const hasAbsence = edited?.absence_type;

                return (
                  <TableCell
                    key={dayStr}
                    align="center"
                    sx={{
                      bgcolor: isDisabled ? 'rgba(0,0,0,0.04)' : isModified ? 'rgba(25, 118, 210, 0.06)' : 'inherit',
                      p: 0.5,
                    }}
                  >
                    {isDisabled ? (
                      <Typography variant="caption" color="text.disabled">-</Typography>
                    ) : (
                      <Stack spacing={0.5} alignItems="center">
                        <Select
                          value={edited?.absence_type || ''}
                          onChange={(e) => onAbsenceChange(key, e.target.value as AbsenceType)}
                          displayEmpty
                          size="small"
                          sx={{ width: 120, fontSize: '0.7rem' }}
                        >
                          <MenuItem value="" sx={{ fontSize: '0.75rem' }}>Presente</MenuItem>
                          <MenuItem value="ferie" sx={{ fontSize: '0.75rem' }}>Ferie</MenuItem>
                          <MenuItem value="permesso" sx={{ fontSize: '0.75rem' }}>Permesso</MenuItem>
                          <MenuItem value="malattia" sx={{ fontSize: '0.75rem' }}>Malattia</MenuItem>
                        </Select>
                        {!edited?.absence_type && (
                          <>
                            <TextField
                              type="time"
                              size="small"
                              value={edited?.check_in || ''}
                              onChange={(e) => onFieldChange(key, 'check_in', e.target.value)}
                              sx={{ width: 110 }}
                              InputProps={{ sx: { fontSize: '0.75rem' } }}
                              placeholder="Ingresso"
                            />
                            <TextField
                              type="time"
                              size="small"
                              value={edited?.check_out || ''}
                              onChange={(e) => onFieldChange(key, 'check_out', e.target.value)}
                              sx={{ width: 110 }}
                              InputProps={{ sx: { fontSize: '0.75rem' } }}
                              placeholder="Uscita"
                            />
                            {edited?.check_in && (
                              <Tooltip title="Copia su tutta la settimana">
                                <IconButton size="small" onClick={() => onCopyDayToWeek(emp.employee_id, dayStr)}>
                                  <CopyIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </>
                        )}
                        {edited?.absence_type === 'permesso' && (
                          <>
                            <Select
                              value={edited?.leave_reason || ''}
                              onChange={(e) => onLeaveReasonChange(key, e.target.value)}
                              displayEmpty
                              size="small"
                              sx={{ width: 120, fontSize: '0.65rem' }}
                            >
                              <MenuItem value="" disabled sx={{ fontSize: '0.7rem' }}>Tipo...</MenuItem>
                              {LEAVE_REASONS.map((r) => (
                                <MenuItem key={r} value={r} sx={{ fontSize: '0.7rem' }}>{r}</MenuItem>
                              ))}
                            </Select>
                            <TextField
                              type="number"
                              size="small"
                              value={edited?.absence_hours || ''}
                              onChange={(e) => onAbsenceHoursChange(key, parseFloat(e.target.value) || 0)}
                              sx={{ width: 60 }}
                              InputProps={{ sx: { fontSize: '0.75rem' }, inputProps: { min: 0.5, max: 8, step: 0.5 } }}
                              placeholder="Ore"
                            />
                          </>
                        )}
                      </Stack>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// --- Funzione export Excel riepilogo mensile ---
const MONTH_NAMES = ['', 'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

const exportMonthlySummary = (data: MonthlyGroupSummary) => {
  const wb = XLSXStyle.utils.book_new();
  const rows: any[][] = [];

  rows.push([`Riepilogo Mensile - ${data.month_name} ${data.year}`]);
  if (data.group_name) rows.push([`Gruppo: ${data.group_name}`]);
  rows.push([]);

  rows.push([
    'Dipendente', 'Matricola', 'Gg. Lavorativi', 'Gg. Presenti', 'Gg. Ferie',
    'Gg. Malattia', 'Ore Permessi', 'Gg. Assenti', 'Ore Attese', 'Ore Lavorate',
    'Delta Ore', 'Ore Straordinario', 'Incompleti', 'Validato',
  ]);

  const totals = { workingDays: 0, present: 0, ferie: 0, malattia: 0, permessi: 0, absent: 0, expected: 0, worked: 0, delta: 0, overtime: 0, incomplete: 0 };

  data.employees.forEach((emp) => {
    rows.push([
      `${emp.cognome} ${emp.nome}`,
      emp.matricola || '-',
      emp.working_days,
      emp.present_days,
      emp.ferie_days,
      emp.malattia_days,
      emp.permessi_hours > 0 ? emp.permessi_hours.toFixed(1) : '-',
      emp.absent_days,
      emp.expected_hours.toFixed(1),
      emp.worked_hours.toFixed(1),
      emp.delta_hours.toFixed(1),
      emp.overtime_hours > 0 ? emp.overtime_hours.toFixed(1) : '-',
      emp.incomplete_days > 0 ? emp.incomplete_days : '-',
      emp.is_validated ? 'Si' : 'No',
    ]);
    totals.workingDays = emp.working_days;
    totals.present += emp.present_days;
    totals.ferie += emp.ferie_days;
    totals.malattia += emp.malattia_days;
    totals.permessi += emp.permessi_hours;
    totals.absent += emp.absent_days;
    totals.expected += emp.expected_hours;
    totals.worked += emp.worked_hours;
    totals.delta += emp.delta_hours;
    totals.overtime += emp.overtime_hours;
    totals.incomplete += emp.incomplete_days;
  });

  rows.push([]);
  rows.push([
    'TOTALE', '', totals.workingDays, totals.present, totals.ferie, totals.malattia,
    totals.permessi > 0 ? totals.permessi.toFixed(1) : '-', totals.absent,
    totals.expected.toFixed(1), totals.worked.toFixed(1), totals.delta.toFixed(1),
    totals.overtime > 0 ? totals.overtime.toFixed(1) : '-', totals.incomplete > 0 ? totals.incomplete : '-', '',
  ]);

  const ws = XLSXStyle.utils.aoa_to_sheet(rows);
  const headerRow = data.group_name ? 3 : 2;
  const headerStyle = {
    fill: { fgColor: { rgb: '1565C0' } },
    font: { bold: true, color: { rgb: 'FFFFFF' } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } },
  };

  for (let c = 0; c < 14; c++) {
    const addr = XLSXStyle.utils.encode_cell({ r: headerRow, c });
    if (ws[addr]) ws[addr].s = headerStyle;
  }

  const totalRow = headerRow + 1 + data.employees.length + 1;
  for (let c = 0; c < 14; c++) {
    const addr = XLSXStyle.utils.encode_cell({ r: totalRow, c });
    if (ws[addr]) ws[addr].s = { ...headerStyle, fill: { fgColor: { rgb: 'E3F2FD' } }, font: { bold: true, color: { rgb: '000000' } } };
  }

  ws['!cols'] = [
    { wch: 25 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 10 },
    { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 12 }, { wch: 12 },
    { wch: 10 }, { wch: 14 }, { wch: 10 }, { wch: 10 },
  ];

  XLSXStyle.utils.book_append_sheet(wb, ws, 'Riepilogo');

  const wbout = XLSXStyle.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([wbout], { type: 'application/octet-stream' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `Riepilogo_${data.month_name}_${data.year}${data.group_name ? `_${data.group_name}` : ''}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// --- Funzione export Excel calendario assenze ---
const ABSENCE_SIGLA: Record<string, string> = { ferie: 'F', malattia: 'M', permesso: 'P' };

const isItalianHolidayJS = (d: Date): boolean => {
  const m = d.getMonth() + 1;
  const day = d.getDate();
  if ((m === 1 && day === 1) || (m === 1 && day === 6) || (m === 4 && day === 25) ||
      (m === 5 && day === 1) || (m === 6 && day === 2) || (m === 8 && day === 15) ||
      (m === 11 && day === 1) || (m === 12 && day === 8) || (m === 12 && day === 25) ||
      (m === 12 && day === 26)) return true;
  return false;
};

const DAY_INITIALS = ['D', 'L', 'M', 'M', 'G', 'V', 'S'];

const exportAbsenceCalendar = (data: AbsenceCalendarData) => {
  const wb = XLSXStyle.utils.book_new();
  const rows: any[][] = [];

  const daysInMonth = new Date(data.year, data.month, 0).getDate();

  // Genera info sui giorni del mese
  const dayInfos: { date: Date; dateStr: string; isWeekend: boolean; isHoliday: boolean }[] = [];
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(data.year, data.month - 1, d);
    const dow = date.getDay();
    dayInfos.push({
      date,
      dateStr: `${data.year}-${String(data.month).padStart(2, '0')}-${String(d).padStart(2, '0')}`,
      isWeekend: dow === 0 || dow === 6,
      isHoliday: isItalianHolidayJS(date),
    });
  }

  // Riga titolo
  rows.push([`Calendario Assenze - ${data.month_name} ${data.year}`]);
  if (data.group_name) rows.push([`Gruppo: ${data.group_name}`]);
  rows.push([]);

  // Header: Dipendente | Matr. | 1 L | 2 M | ... | Tot F | Tot M | Tot P
  const headerRow: string[] = ['Dipendente', 'Matr.'];
  for (let d = 0; d < daysInMonth; d++) {
    const dayNum = d + 1;
    const dow = dayInfos[d].date.getDay();
    headerRow.push(`${dayNum} ${DAY_INITIALS[dow]}`);
  }
  headerRow.push('Tot F', 'Tot M', 'Tot P');
  rows.push(headerRow);

  // Totali globali
  const globalTotals = { F: 0, M: 0, P: 0 };

  // Righe dipendenti
  data.employees.forEach((emp) => {
    const row: (string | number)[] = [`${emp.cognome} ${emp.nome}`, emp.matricola || '-'];
    let totF = 0, totM = 0, totP = 0;
    for (let d = 0; d < daysInMonth; d++) {
      const info = dayInfos[d];
      if (info.isWeekend || info.isHoliday) {
        row.push('');
      } else {
        const absType = emp.absences[info.dateStr] || '';
        const sigla = ABSENCE_SIGLA[absType] || '';
        row.push(sigla);
        if (sigla === 'F') totF++;
        else if (sigla === 'M') totM++;
        else if (sigla === 'P') totP++;
      }
    }
    row.push(totF || '', totM || '', totP || '');
    globalTotals.F += totF;
    globalTotals.M += totM;
    globalTotals.P += totP;
    rows.push(row);
  });

  // Riga totali
  rows.push([]);
  const totalsRow: (string | number)[] = ['TOTALE', ''];
  for (let d = 0; d < daysInMonth; d++) totalsRow.push('');
  totalsRow.push(globalTotals.F || '', globalTotals.M || '', globalTotals.P || '');
  rows.push(totalsRow);

  const ws = XLSXStyle.utils.aoa_to_sheet(rows);
  const headerRowIdx = data.group_name ? 3 : 2;
  const totalCols = 2 + daysInMonth + 3;

  // Stili
  const headerStyle = {
    fill: { fgColor: { rgb: '1565C0' } },
    font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 9 },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } },
  };
  const weekendStyle = {
    fill: { fgColor: { rgb: 'E0E0E0' } },
    font: { color: { rgb: '9E9E9E' }, sz: 9 },
    alignment: { horizontal: 'center' },
    border: { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } },
  };
  const ferieStyle = {
    fill: { fgColor: { rgb: 'BBDEFB' } },
    font: { bold: true, color: { rgb: '0D47A1' }, sz: 10 },
    alignment: { horizontal: 'center' },
    border: { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } },
  };
  const malattiaStyle = {
    fill: { fgColor: { rgb: 'FFE0B2' } },
    font: { bold: true, color: { rgb: 'E65100' }, sz: 10 },
    alignment: { horizontal: 'center' },
    border: { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } },
  };
  const permessoStyle = {
    fill: { fgColor: { rgb: 'E1BEE7' } },
    font: { bold: true, color: { rgb: '6A1B9A' }, sz: 10 },
    alignment: { horizontal: 'center' },
    border: { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } },
  };
  const cellBorder = {
    border: { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } },
    alignment: { horizontal: 'center' },
    font: { sz: 9 },
  };
  const totalsStyle = {
    fill: { fgColor: { rgb: 'E3F2FD' } },
    font: { bold: true, sz: 9 },
    alignment: { horizontal: 'center' },
    border: { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } },
  };

  // Applica stili header
  for (let c = 0; c < totalCols; c++) {
    const addr = XLSXStyle.utils.encode_cell({ r: headerRowIdx, c });
    if (ws[addr]) {
      ws[addr].s = headerStyle;
      // Header weekend/festivo in grigio
      if (c >= 2 && c < 2 + daysInMonth) {
        const info = dayInfos[c - 2];
        if (info.isWeekend || info.isHoliday) {
          ws[addr].s = { ...headerStyle, fill: { fgColor: { rgb: '78909C' } } };
        }
      }
    }
  }

  // Applica stili alle celle dati
  const dataStartRow = headerRowIdx + 1;
  const dataEndRow = dataStartRow + data.employees.length;
  for (let r = dataStartRow; r < dataEndRow; r++) {
    for (let c = 0; c < totalCols; c++) {
      const addr = XLSXStyle.utils.encode_cell({ r, c });
      if (c < 2) {
        // Colonne nome/matricola
        if (ws[addr]) ws[addr].s = { ...cellBorder, alignment: { horizontal: 'left' }, font: { sz: 9 } };
      } else if (c >= 2 && c < 2 + daysInMonth) {
        const info = dayInfos[c - 2];
        if (info.isWeekend || info.isHoliday) {
          if (!ws[addr]) ws[addr] = { t: 's', v: '' };
          ws[addr].s = weekendStyle;
        } else {
          const val = ws[addr]?.v;
          if (val === 'F') ws[addr].s = ferieStyle;
          else if (val === 'M') ws[addr].s = malattiaStyle;
          else if (val === 'P') ws[addr].s = permessoStyle;
          else {
            if (!ws[addr]) ws[addr] = { t: 's', v: '' };
            ws[addr].s = cellBorder;
          }
        }
      } else {
        // Colonne totali
        if (ws[addr]) ws[addr].s = totalsStyle;
      }
    }
  }

  // Stile riga totali globali
  const totalsRowIdx = dataEndRow + 1;
  for (let c = 0; c < totalCols; c++) {
    const addr = XLSXStyle.utils.encode_cell({ r: totalsRowIdx, c });
    if (ws[addr]) ws[addr].s = { ...totalsStyle, font: { bold: true, sz: 10 } };
  }

  // Larghezze colonne
  const cols: { wch: number }[] = [{ wch: 22 }, { wch: 8 }];
  for (let d = 0; d < daysInMonth; d++) cols.push({ wch: 4 });
  cols.push({ wch: 6 }, { wch: 6 }, { wch: 6 });
  ws['!cols'] = cols;

  XLSXStyle.utils.book_append_sheet(wb, ws, 'Assenze');

  // Legenda su foglio separato
  const legendRows = [
    ['Legenda'],
    [],
    ['Sigla', 'Significato'],
    ['F', 'Ferie (giornata intera)'],
    ['M', 'Malattia (giornata intera)'],
    ['P', 'Permesso'],
    [],
    ['', 'Celle grigie = Weekend / Festivo'],
  ];
  const wsLegend = XLSXStyle.utils.aoa_to_sheet(legendRows);
  // Stili legenda
  const legendFAddr = XLSXStyle.utils.encode_cell({ r: 3, c: 0 });
  const legendMAddr = XLSXStyle.utils.encode_cell({ r: 4, c: 0 });
  const legendPAddr = XLSXStyle.utils.encode_cell({ r: 5, c: 0 });
  if (wsLegend[legendFAddr]) wsLegend[legendFAddr].s = ferieStyle;
  if (wsLegend[legendMAddr]) wsLegend[legendMAddr].s = malattiaStyle;
  if (wsLegend[legendPAddr]) wsLegend[legendPAddr].s = permessoStyle;
  wsLegend['!cols'] = [{ wch: 8 }, { wch: 30 }];
  XLSXStyle.utils.book_append_sheet(wb, wsLegend, 'Legenda');

  const wbout = XLSXStyle.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([wbout], { type: 'application/octet-stream' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `Assenze_${data.month_name}_${data.year}${data.group_name ? `_${data.group_name}` : ''}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// --- Sotto-componente Vista Mensile ---
interface MonthlyViewProps {
  data: MonthlyGroupSummary | null;
  loading: boolean;
  exporting: boolean;
  exportingAbsences: boolean;
  applyingMonthlyTemplate: boolean;
  year: number;
  month: number;
  selectedGroupId: number;
  onYearChange: (y: number) => void;
  onMonthChange: (m: number) => void;
  onRefresh: () => void;
  onApplyAll: () => void;
  onExport: () => void;
  onExportAbsences: () => void;
}

const MonthlyView: React.FC<MonthlyViewProps> = ({ data, loading, exporting, exportingAbsences, applyingMonthlyTemplate, year, month, onYearChange, onMonthChange, onRefresh, onApplyAll, onExport, onExportAbsences }) => {
  const totals = data?.employees.reduce((acc, emp) => ({
    expected: acc.expected + emp.expected_hours,
    worked: acc.worked + emp.worked_hours,
    overtime: acc.overtime + emp.overtime_hours,
    present: acc.present + emp.present_days,
    absent: acc.absent + emp.absent_days,
    ferie: acc.ferie + emp.ferie_days,
    malattia: acc.malattia + emp.malattia_days,
    permessi: acc.permessi + emp.permessi_hours,
    incomplete: acc.incomplete + emp.incomplete_days,
    delta: acc.delta + emp.delta_hours,
    validated: acc.validated + (emp.is_validated ? 1 : 0),
  }), { expected: 0, worked: 0, overtime: 0, present: 0, absent: 0, ferie: 0, malattia: 0, permessi: 0, incomplete: 0, delta: 0, validated: 0 });

  return (
    <>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Anno</InputLabel>
            <Select value={year} label="Anno" onChange={(e) => onYearChange(Number(e.target.value))}>
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Mese</InputLabel>
            <Select value={month} label="Mese" onChange={(e) => onMonthChange(Number(e.target.value))}>
              {MONTH_NAMES.slice(1).map((name, i) => (
                <MenuItem key={i + 1} value={i + 1}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton onClick={onRefresh}><RefreshIcon /></IconButton>
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              color="warning"
              startIcon={applyingMonthlyTemplate ? <CircularProgress size={16} color="inherit" /> : <ScheduleIcon />}
              onClick={onApplyAll}
              disabled={loading || applyingMonthlyTemplate || exportingAbsences || exporting}
            >
              Applica tutti
            </Button>
            <Button
              variant="outlined"
              startIcon={exportingAbsences ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
              onClick={onExportAbsences}
              disabled={loading || exportingAbsences || exporting}
            >
              Esporta Assenze
            </Button>
            <Button
              variant="contained"
              startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
              onClick={onExport}
              disabled={loading || exporting || !data || data.employees.length === 0}
            >
              Esporta Excel
            </Button>
          </Box>
        </Box>

        {totals && data && data.employees.length > 0 && (
          <Box display="flex" gap={1.5} mt={2} flexWrap="wrap">
            <Chip label={`Dipendenti: ${data.employees.length}`} variant="outlined" />
            <Chip label={`Presenze: ${totals.present}`} color="success" size="small" />
            <Chip label={`Ferie: ${totals.ferie}`} color="info" size="small" />
            <Chip label={`Malattia: ${totals.malattia}`} color="warning" size="small" />
            {totals.permessi > 0 && <Chip label={`Permessi: ${totals.permessi.toFixed(1)}h`} color="secondary" size="small" />}
            <Chip label={`Validati: ${totals.validated}/${data.employees.length}`} color={totals.validated === data.employees.length ? 'success' : 'default'} size="small" />
          </Box>
        )}
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
      ) : !data || data.employees.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">Nessun dato trovato per il periodo selezionato.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 700, position: 'sticky', left: 0, bgcolor: 'primary.main', zIndex: 2 }}>Dipendente</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }} align="center">Gg. Lav.</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }} align="center">Gg. Pres.</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }} align="center">Ferie</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }} align="center">Malattia</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }} align="center">Permessi</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }} align="center">Assenti</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }} align="right">Ore Attese</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }} align="right">Ore Lavorate</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }} align="right">Delta</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }} align="right">Straordinario</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }} align="center">Stato</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.employees.map((emp) => (
                <TableRow
                  key={emp.employee_id}
                  hover
                  sx={{
                    bgcolor: emp.incomplete_days > 0 ? 'rgba(255, 152, 0, 0.04)' : undefined,
                  }}
                >
                  <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1, borderRight: '1px solid rgba(0,0,0,0.12)' }}>
                    <Typography variant="body2" fontWeight={600}>{emp.cognome} {emp.nome}</Typography>
                    <Typography variant="caption" color="text.secondary">{emp.matricola || ''}</Typography>
                  </TableCell>
                  <TableCell align="center">{emp.working_days}</TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight={600} color={emp.present_days > 0 ? 'success.main' : 'text.secondary'}>
                      {emp.present_days}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {emp.ferie_days > 0 ? (
                      <Chip label={emp.ferie_days} size="small" color="info" variant="outlined" />
                    ) : '-'}
                  </TableCell>
                  <TableCell align="center">
                    {emp.malattia_days > 0 ? (
                      <Chip label={emp.malattia_days} size="small" color="warning" variant="outlined" />
                    ) : '-'}
                  </TableCell>
                  <TableCell align="center">
                    {emp.permessi_hours > 0 ? `${emp.permessi_hours.toFixed(1)}h` : '-'}
                  </TableCell>
                  <TableCell align="center">
                    {emp.absent_days > 0 ? (
                      <Typography variant="body2" color="error.main" fontWeight={600}>{emp.absent_days}</Typography>
                    ) : '-'}
                  </TableCell>
                  <TableCell align="right">{emp.expected_hours.toFixed(1)}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600}>{emp.worked_hours.toFixed(1)}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color={emp.delta_hours >= 0 ? 'success.main' : 'error.main'}
                    >
                      {emp.delta_hours >= 0 ? '+' : ''}{emp.delta_hours.toFixed(1)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {emp.overtime_hours > 0 ? `${emp.overtime_hours.toFixed(1)}` : '-'}
                  </TableCell>
                  <TableCell align="center">
                    {emp.is_validated ? (
                      <Tooltip title="Cartellino validato">
                        <CheckCircleIcon color="success" fontSize="small" />
                      </Tooltip>
                    ) : (
                      <Tooltip title="In attesa di validazione">
                        <HourglassIcon color="warning" fontSize="small" />
                      </Tooltip>
                    )}
                    {emp.incomplete_days > 0 && (
                      <Chip label={`${emp.incomplete_days} incompleti`} size="small" color="warning" variant="outlined" sx={{ ml: 0.5, fontSize: '0.65rem' }} />
                    )}
                  </TableCell>
                </TableRow>
              ))}

              {/* Riga totali */}
              {totals && (
                <TableRow sx={{ bgcolor: 'rgba(21, 101, 192, 0.06)' }}>
                  <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'rgba(21, 101, 192, 0.06)', zIndex: 1, fontWeight: 700, borderRight: '1px solid rgba(0,0,0,0.12)' }}>
                    TOTALE ({data.employees.length} dipendenti)
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>{data.employees[0]?.working_days || '-'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>{totals.present}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>{totals.ferie > 0 ? totals.ferie : '-'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>{totals.malattia > 0 ? totals.malattia : '-'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>{totals.permessi > 0 ? `${totals.permessi.toFixed(1)}h` : '-'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>{totals.absent > 0 ? totals.absent : '-'}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{totals.expected.toFixed(1)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{totals.worked.toFixed(1)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: totals.delta >= 0 ? 'success.main' : 'error.main' }}>
                    {totals.delta >= 0 ? '+' : ''}{totals.delta.toFixed(1)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{totals.overtime > 0 ? totals.overtime.toFixed(1) : '-'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>{totals.validated}/{data.employees.length}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

export default GuidedPresenceEntry;
